import * as Dropdown from '@radix-ui/react-dropdown-menu';
import { Checkmark, ChevronDown } from '@remarkable/ark-icons';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { Fragment, useMemo } from 'react';
import { ComponentLocations } from 'src/analytics/trackingTypes';
import type { FleetUser } from 'src/api/endpoints/cloudEnterprise.types';
import {
  queryEnterpriseRoles,
  useAddRole,
  useDeleteRole,
} from 'src/api/queries/enterpriseAdmin';
import { useAccessTokenClaims } from 'src/api/queries/user';
import { Button, Divider } from 'src/components';
import { DropdownMenu } from 'src/components/DropdownMenu';
import { userHelpers } from 'src/utils/userHelpers';
import { mergeRoleLists } from '../utils/mergeRoleLists';

export function RoleSelector({ member }: { member: FleetUser }) {
  const accessTokenClaims = useAccessTokenClaims();
  const canReadRoles = userHelpers.hasPermissionReadRoleAssignments(
    accessTokenClaims.data
  );
  const hasPermissionToAddRole = userHelpers.hasPermissionCreateRoleAssignments(
    accessTokenClaims.data
  );

  const hasPermissionToRemoveRole =
    userHelpers.hasPermissionDeleteRoleAssignments(accessTokenClaims.data);

  const canChangeRole = (userAlreadyHaveRole?: boolean) => {
    return userAlreadyHaveRole
      ? hasPermissionToRemoveRole
      : hasPermissionToAddRole;
  };

  const addRole = useAddRole();
  const deleteRole = useDeleteRole();
  const roles = useQuery({
    ...queryEnterpriseRoles(),
    enabled: canReadRoles,
  });
  const memberRoles = useMemo(() => {
    return member.roles || [];
  }, [member.roles]);

  const roleList = useMemo(() => {
    return mergeRoleLists(roles.data || [], memberRoles);
  }, [roles.data, memberRoles]);

  const isMemberDeactivated = member.status === 'deactivated';
  const isMemberMarkedForDeletion = member.status === 'deletion_mark';

  // cant change roles of deactivated members
  const showRolesAction = !isMemberDeactivated && !isMemberMarkedForDeletion;

  const currentRole = memberRoles[0];
  // in that case we don't show the menu at all as no actions are available
  if (!showRolesAction || isMemberMarkedForDeletion) {
    return (
      <div className="mx-12 text-muted" data-cy="disabled-role-selector">
        {currentRole?.name || ''}
      </div>
    );
  }

  return (
    <DropdownMenu
      trigger={
        <Button
          variant="tertiary-neutral"
          className="!rounded-sm w-full max-w-128 data-[state=open]:bg-interaction-tertiary-active"
          size="small"
          data-cy={`actions-button-cy_${member.email ?? 'unknown'}`}
          analytics={{
            text: 'DotsThreeVertical icon',
            location: ComponentLocations.FLEET.FLEET_MEMBERS_TABLE,
            action: 'open member actions menu',
          }}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          loading={addRole.isPending || deleteRole.isPending}
          disabled={addRole.isPending || deleteRole.isPending}
        >
          <div className="flex w-full items-center justify-between gap-8 text-left">
            {currentRole ? (
              <span className="font-regular">{currentRole.name}</span>
            ) : (
              <span className="shrink-0 font-regular text-muted">
                Select role
              </span>
            )}{' '}
            <ChevronDown />
          </div>
        </Button>
      }
      onClick={(e) => e.stopPropagation()}
    >
      {roleList.map((role, index) => {
        const disableRole =
          role.isNotGloballyAvailableRole ||
          canChangeRole(role.hasRole) === false;
        return (
          <Fragment key={role.id}>
            {index !== 0 && <Divider className="mx-12 my-4" />}
            <Dropdown.DropdownMenuCheckboxItem
              checked={role.hasRole}
              onCheckedChange={(newState) => {
                if (disableRole) return;

                if (newState === true) {
                  addRole.mutate({
                    memberId: member.id,
                    roleId: role.id,
                  });
                } else {
                  deleteRole.mutate({
                    memberId: member.id,
                    roleId: role.id,
                  });
                }
              }}
              onKeyDown={(e) => e.stopPropagation()}
              className={clsx('flex flex-col px-12 py-4', {
                '!cursor-not-allowed text-muted opacity-50': disableRole,
                'cursor-pointer hover:bg-paper-2': !disableRole,
              })}
              aria-label={role.name}
              aria-description={role.description}
              disabled={disableRole}
              aria-disabled={disableRole}
            >
              <div className="flex items-center gap-8">
                <Dropdown.ItemIndicator>
                  <Checkmark />
                </Dropdown.ItemIndicator>
                {role.name}
              </div>
              {role.description && (
                <div className="mt-8 max-w-320 text-xs text-muted">
                  {role.description}
                </div>
              )}
            </Dropdown.DropdownMenuCheckboxItem>
          </Fragment>
        );
      })}
    </DropdownMenu>
  );
}
